import { AuthError } from './errors.ts'
import {
  initiateCustomAuth,
  respondToCustomChallenge,
  revokeToken,
  toTokenResponse,
} from './idp-operations.ts'
import { stringClaim } from './jwt.ts'
import type { AuthKernel } from './kernel.ts'
import type { DeviceAccount, DeviceAccounts, Leaving } from './types.ts'

// The accounts open on this device (spec #278, #302): which one is in use, switching without a password,
// a tutore adding a child with the API's proof, and leaving one account or all of them.

/** What `POST /v1/people/{id}/sign-in-grant` answers: the child's username and the proof. */
export interface DelegationGrant {
  readonly username: string
  readonly answer: string
}

export interface AccountsApi {
  /** The same object until something changes, so it can feed `useSyncExternalStore`. */
  get(): DeviceAccounts
  subscribe(listener: () => void): () => void
  /** With the account's own refresh token. A spent token takes that account away; any failure leaves the one in use. */
  switchTo(sub: string): Promise<void>
  /** The child joins this device; the tutore stays in use. A failure adds nothing and changes nothing,
   *  and so do tokens that carry no refresh token to keep the child by. */
  addDelegated(
    grant: DelegationGrant,
    child: { readonly name: string; readonly personId: number },
  ): Promise<DeviceAccount>
  /** `RevokeToken`, this device only. Rejects when the revocation failed: the account is gone either way. */
  signOut(sub: string): Promise<void>
  signOutAll(): Promise<void>
  /** The two above, settled as soon as the accounts are off this device: the revocations go on behind. */
  leave(sub: string): Promise<Leaving>
  leaveAll(): Promise<Leaving>
}

/** Every revocation is tried; the first that failed is the one reported. */
async function allRevoked(revocations: readonly Promise<void>[]): Promise<void> {
  const failed = (await Promise.allSettled(revocations)).find(
    (outcome) => outcome.status === 'rejected',
  )
  if (failed !== undefined) throw failed.reason
}

function unavailable(cause: unknown): AuthError {
  return new AuthError('delegation_unavailable', 'Cognito cannot take a proof here', { cause })
}

function refused(message: string): AuthError {
  return new AuthError('delegation_refused', message)
}

export function createAccountsApi(kernel: AuthKernel): AccountsApi {
  const { clientId } = kernel.config

  async function leave(sub: string): Promise<Leaving> {
    const removed = await kernel.forget(sub)
    return {
      revoked:
        removed === undefined ? Promise.resolve() : revokeToken(clientId, removed.refreshToken),
    }
  }

  async function leaveAll(): Promise<Leaving> {
    const removed = await kernel.forgetAll()
    return {
      revoked: allRevoked(removed.map((account) => revokeToken(clientId, account.refreshToken))),
    }
  }

  // Every refusal before the proof is read is the pool's: no trigger, no `ALLOW_CUSTOM_AUTH`, an outage.
  async function challengeFor(username: string): Promise<{ session: string; username: string }> {
    const challenge = await initiateCustomAuth(clientId, username).catch((error: unknown) => {
      throw unavailable(error)
    })
    if (challenge.ChallengeName !== 'CUSTOM_CHALLENGE' || challenge.Session === undefined) {
      throw unavailable(new Error(`Cognito asked for "${challenge.ChallengeName ?? 'nothing'}"`))
    }
    return {
      session: challenge.Session,
      username: challenge.ChallengeParameters?.USERNAME ?? username,
    }
  }

  async function answered(grant: DelegationGrant) {
    const challenge = await challengeFor(grant.username)
    const response = await respondToCustomChallenge(clientId, {
      ...challenge,
      answer: grant.answer,
    }).catch((error: unknown) => {
      throw error instanceof AuthError && error.code === 'delegation_refused'
        ? error
        : unavailable(error)
    })
    const result = response.AuthenticationResult
    if (result === undefined) throw refused('Cognito asked for another round')
    // The proof names one child: tokens of anybody else are not what the tutore asked for.
    if (result.IdToken === undefined || stringClaim(result.IdToken, 'sub') !== grant.username) {
      throw refused('The tokens are not the account the proof named')
    }
    return toTokenResponse(result)
  }

  return {
    get: kernel.accounts,
    subscribe: kernel.subscribe,
    switchTo: kernel.switchTo,

    async addDelegated(grant, child) {
      const tokens = await answered(grant)
      // Held on this phone by its refresh token alone: without one the account would be reported
      // added and be gone with the tab.
      if (tokens.refresh_token === undefined) throw refused('Cognito issued no refresh token')
      return kernel.applyTokens(tokens, {
        kind: 'delegated',
        activate: false,
        name: child.name,
        personId: child.personId,
        addedBy: kernel.accounts().active ?? undefined,
      })
    },

    async signOut(sub) {
      const { revoked } = await leave(sub)
      await revoked
    },

    async signOutAll() {
      const { revoked } = await leaveAll()
      await revoked
    },

    leave,
    leaveAll,
  }
}
